import React, { useEffect } from 'react'
import LayoutActions from '../redux/actions/LayoutActions'
import { Switch, withRouter, Route, Redirect } from 'react-router-dom'
import UserRoute from '../routers/UserRoute'
import { connect } from 'react-redux'
import { Box } from '@mui/system'
import { layoutPageComponents as components } from './components/layoutPageComponents'
import { subscribeToRouteSync } from '../utils/routeSyncBridge'

const stripBase = (path) => {
    const base = window.__ADMIN_STOCK_BASENAME__ || '';
    if (!base) return path;
    if (path === base || path === `${base}/`) return '/';
    if (path.startsWith(`${base}/`)) return path.slice(base.length) || '/';
    return path;
};

/**
 * Nested layout for the print-settings group (Transfer / Wastage / PO print
 * designs). Same data-driven routing as StockListsLayout — it renders each
 * `permission.subPage` tab into the shared components registry and redirects
 * the base route to the first accessible tab.
 *
 * Kept as a dedicated component (rather than reusing StockListsLayout) so any
 * print-specific shared chrome can live here without affecting the stock-lists
 * group. Add such chrome around the <Switch> below.
 */
function StockPrintLayout({
    setPathCheck,
    permission,
    reformat
}) {
    const access = reformat(permission.subPage)
        .sort((a, b) => {
            // Check if either 'order' is null
            if (a.order === null && b.order === null) {
                return 0; // Keep the order unchanged if both are null
            } else if (a.order === null) {
                return 1; // Place 'a' (null) after 'b' (non-null)
            } else if (b.order === null) {
                return -1; // Place 'b' (null) after 'a' (non-null)
            }
            // Sort non-null 'order' values normally
            return a.order - b.order;
        });

    // Tab labels are `page-names.<key>` in the resto-admin platform — log the keys so a "No Translation"
    // tab can be matched to the dictionary row it needs.
    console.log('[StockPrintLayout] tab keys', access.map((p) => p.key))

    useEffect(() => {
        // For microfrontend, sync with parent route changes via window.location.pathname
        const checkPathname = () => {
            const strippedPathname = stripBase(window.location.pathname);

            if (strippedPathname === permission.route || strippedPathname.startsWith(permission.route + '/')) {
                setPathCheck(permission.route);
            }
        };

        return subscribeToRouteSync(checkPathname);
    }, [permission.route, setPathCheck])

    return (
        <div style={{ height: '100%' }}>
            <Box sx={{ height: '100%' }}>
                {/* Add shared print-settings chrome here (e.g. a common preview pane) */}
                <Switch>
                    <Route
                        exact
                        path={permission.route}
                        render={() => <Redirect to={access[0]?.route || `${permission.route}/label-print-settings`} />}
                    />
                    {access.map((permission2, i) =>
                        <UserRoute key={i} exact path={permission2.route} component={components[permission2.key]} permission={permission2} />
                    )}
                    {/* TEMP: reach-by-URL route until the `labelPrintSettings` page-module row is seeded
                        under this print-settings group. It lives inside this layout's own Switch (not
                        SettingsLayout's) because this group matches its prefix non-exactly and would
                        otherwise swallow the path into the not-found redirect below. The SMA Print
                        Labels screen opens it through PrintSettingsShortcut once the row exists. */}
                    <UserRoute
                        exact
                        path={`${permission.route}/label-print-settings`}
                        component={components.labelPrintSettings}
                        permission={permission}
                    />
                    {/* TEMP: same reach-by-URL fallback for the two statement templates, relative to
                        wherever this group lives (SettingsLayout only covers the old
                        /stock-app/settings/print-settings location). */}
                    <UserRoute
                        exact
                        path={`${permission.route}/customer-statement-print-settings`}
                        component={components.customerStatementPrintSettings}
                        permission={permission}
                    />
                    <UserRoute
                        exact
                        path={`${permission.route}/statement-print-settings`}
                        component={components.printSupplierStatementSettings}
                        permission={permission}
                    />
                    <Redirect from="*" to="/not-found" />
                </Switch>
            </Box>
        </div>
    )
}
const mapStateToProps = (state) => ({
})
const mapDispatchToProps = (dispatch) => ({
    setPathCheck: (data) => dispatch(LayoutActions.setPathCheck(data))
})
export default connect(mapStateToProps, mapDispatchToProps)(withRouter(StockPrintLayout))
